import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@core/lib/utils";

// Déclaration pour la fonction de préchargement
declare global {
  interface Window {
    preloadPollCreator?: () => void;
  }
}
import {
  Calendar,
  ClipboardList,
  MessageSquare,
  Users,
  Vote,
  BarChart3,
  Trash2,
  Check,
  Tag,
  Folder,
  MoreVertical,
  Settings,
  Share2,
  Download,
  Edit,
  Copy,
  Lock,
  Archive,
  ChevronDown,
  MapPin,
} from "lucide-react";
import { ConversationItem } from "./types";
import { getStatusColor, getStatusLabel, getThemeColors } from "./utils";
import { useConversations } from "@core/hooks/useConversations";
import { useToast } from "@core/hooks/use-toast";
import { getAllTags } from "@core/lib/storage/TagStorage";
import { getAllFolders, getFolderById } from "@core/lib/storage/FolderStorage";
import { ManageTagsFolderDialog } from "./ManageTagsFolderDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@core/components/ui/dropdown-menu";
import { Button } from "@core/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@core/components/ui/alert-dialog";
import {
  buildPublicLink,
  copyToClipboard,
  deleteVotesByPollId,
  addPoll,
} from "@core/lib/pollStorage";
import type { UnifiedPoll } from "@core/lib/pollStorageUnified";
import { pollService } from "@core/services/PollService";
import {
  exportFormPollToCSV,
  exportFormPollToPDF,
  exportFormPollToJSON,
  exportFormPollToMarkdown,
  hasExportableData,
} from "@core/lib/exports";
import { usePollDeletionCascade } from "@core/hooks/usePollDeletionCascade";
import { createConversationForPoll } from "@core/lib/ConversationPollLink";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import { formatDateFull } from "@core/lib/date-utils";

interface ConversationCardProps {
  item: ConversationItem;
  isSelected?: boolean;
  onToggleSelection?: () => void;
  onRefresh: () => void;
}

export const ConversationCard: React.FC<ConversationCardProps> = ({
  item,
  isSelected = false,
  onToggleSelection,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("polls");
  const { toast } = useToast();
  const { deleteConversation, updateConversation } = useConversations();
  const { deletePollWithCascade } = usePollDeletionCascade();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTagsFolderDialog, setShowTagsFolderDialog] = useState(false);

  // Dialog de confirmation générique : remplace tous les `window.confirm`
  // (qui bloquaient le main thread et rendaient les actions intestables).
  type ConfirmConfig = {
    title: string;
    description: string;
    confirmLabel: string;
    destructive?: boolean;
    onConfirm: () => Promise<void> | void;
  };
  const [confirmConfig, setConfirmConfig] = useState<ConfirmConfig | null>(
    null,
  );
  const [isConfirming, setIsConfirming] = useState(false);

  const runConfirm = async () => {
    if (!confirmConfig) return;
    setIsConfirming(true);
    try {
      await confirmConfig.onConfirm();
      setConfirmConfig(null);
    } finally {
      setIsConfirming(false);
    }
  };
  const [isCopied, setIsCopied] = useState(false);
  const preloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tags = getAllTags();
  const folders = getAllFolders();
  const folder = item.folderId ? getFolderById(item.folderId) : null;
  const theme = getThemeColors(item.poll?.type || "date");
  const [isExpanded, setIsExpanded] = useState(false);

  const handleCardClick = (e: React.MouseEvent) => {
    // Empêcher la navigation si on clique sur un élément interactif
    const target = e.target as HTMLElement;

    // Vérifier si le clic est sur un élément interactif ou son parent
    if (
      target.closest("button") ||
      target.closest('[data-testid*="navigate"]') ||
      target.closest('[data-testid*="button"]') ||
      target.closest("a") ||
      target.closest(".dropdown-menu") ||
      target.closest(".dropdown-content") ||
      target.closest('[role="button"]') ||
      target.closest('[role="menuitem"]') ||
      target.tagName === "BUTTON" ||
      target.tagName === "A"
    ) {
      return;
    }

    // Mobile: Toggle accordion
    if (window.innerWidth < 640) {
      setIsExpanded(!isExpanded);
      return;
    }

    // Desktop: NE PLUS naviguer vers workspace (selon la demande de l'utilisateur)
    // La zone vide de la tuile ne doit plus déclencher de navigation
    return;
  };

  const handleDeleteConversation = () => {
    setConfirmConfig({
      title: t("conversation_card.delete_conversation_confirm_title"),
      description: t(
        "conversation_card.delete_conversation_confirm_description",
      ),
      confirmLabel: t("conversation_card.delete"),
      destructive: true,
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await deleteConversation.mutateAsync(item.id);
          toast({
            title: t("conversation_card.conversation_deleted_title"),
            description: t(
              "conversation_card.conversation_deleted_description",
            ),
          });
          onRefresh();
        } catch (error) {
          toast({
            title: t("conversation_card.error"),
            description: t("conversation_card.conversation_delete_error"),
            variant: "destructive",
          });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  // Handlers pour les actions de poll (extraits de PollActions)
  const handleCopyLink = async () => {
    if (!item.poll) return;
    try {
      const url = buildPublicLink(item.poll.slug);
      await copyToClipboard(url);
      setIsCopied(true);
      toast({
        title: t("conversation_card.link_copied_title"),
        description: t("conversation_card.link_copied_description"),
      });
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast({
        title: t("conversation_card.error"),
        description: t("conversation_card.link_copy_error"),
        variant: "destructive",
      });
    }
  };

  const handlePreloadEdit = () => {
    if (!item.poll) return;
    // Précharger PollCreator si c'est un sondage de dates (pas formulaire)
    if (item.poll.type !== "form") {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
      preloadTimeoutRef.current = setTimeout(() => {
        if (typeof window.preloadPollCreator === "function") {
          window.preloadPollCreator();
        }
      }, 300);
    }
  };

  const handleMouseLeaveEdit = () => {
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
      preloadTimeoutRef.current = null;
    }
  };

  const handleEdit = () => {
    if (!item.poll) return;
    if (item.poll.type === "form") {
      navigate(
        `/workspace/form?edit=${item.poll.id}&conversationId=${item.id}`,
      );
    } else {
      navigate(
        `/workspace/date?edit=${item.poll.id}&conversationId=${item.id}`,
      );
    }
  };

  const handleDuplicate = async () => {
    if (!item.poll) return;
    try {
      const dup = await pollService.duplicatePoll(item.poll as any);
      // Le poll est déjà créé dans Supabase, plus besoin de addPoll()
      // Gérer les types non supportés par createConversationForPoll
      const rawPollType = dup.type || "date";
      if (rawPollType === "date" || rawPollType === "availability") {
        // Passer isDraft=true pour créer une conversation en mode "active" (brouillon)
        createConversationForPoll(dup.id, dup.title, rawPollType, true);
      }
      toast({
        title: t("conversation_card.poll_duplicated_title"),
        description: t("conversation_card.poll_duplicated_description", {
          title: dup.title,
        }),
      });

      // Rafraîchir le dashboard après duplication
      onRefresh();
    } catch (error) {
      logError(error, {
        component: "ConversationCard",
        operation: "duplicatePoll",
      });
      toast({
        title: t("conversation_card.error"),
        description: t("conversation_card.poll_duplicate_error"),
        variant: "destructive",
      });
    }
  };

  const handleArchive = () => {
    if (!item.poll) return;
    const pollId = item.poll.id;
    const pollType = item.poll.type;
    const typeLabel =
      pollType === "form"
        ? "form"
        : pollType === "date"
          ? "date poll"
          : pollType === "quizz"
            ? "quizz"
            : "poll";
    setConfirmConfig({
      title: t("conversation_card.archive_poll_confirm_title"),
      description: t("conversation_card.archive_poll_confirm_description"),
      confirmLabel: t("conversation_card.archive"),
      onConfirm: async () => {
        try {
          await pollService.updatePoll(pollId, { status: "archived" });
          toast({
            title: t("conversation_card.poll_archived_title", {
              type: typeLabel,
            }),
            description: t("conversation_card.poll_archived_description", {
              type: typeLabel,
            }),
          });
          onRefresh();
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("pollArchived", { detail: { pollId } }),
            );
          }
        } catch (error) {
          logError(error, {
            component: "ConversationCard",
            operation: "archivePoll",
            pollId,
          });
          toast({
            title: t("conversation_card.error"),
            description: t("conversation_card.poll_archive_error", {
              type: typeLabel,
            }),
            variant: "destructive",
          });
        }
      },
    });
  };

  const handleClose = () => {
    if (!item.poll) return;
    const poll = item.poll;
    const pollType = poll.type;
    const typeLabel =
      pollType === "form"
        ? "form"
        : pollType === "date"
          ? "date poll"
          : pollType === "quizz"
            ? "quizz"
            : "poll";
    setConfirmConfig({
      title: t("conversation_card.close_poll_confirm_title"),
      description: t("conversation_card.close_poll_confirm_description"),
      confirmLabel: t("conversation_card.close"),
      destructive: true,
      onConfirm: () => {
        try {
          const updatedPoll = {
            ...poll,
            status: "closed" as const,
            updated_at: new Date().toISOString(),
          };
          addPoll(updatedPoll);
          toast({
            title: t("conversation_card.poll_closed_title", {
              type: typeLabel,
            }),
            description: t("conversation_card.poll_closed_description", {
              type: typeLabel,
            }),
          });
          onRefresh();
        } catch {
          toast({
            title: t("conversation_card.error"),
            description: t("conversation_card.poll_close_error", {
              type: typeLabel,
            }),
            variant: "destructive",
          });
        }
      },
    });
  };

  const handleDeletePoll = () => {
    if (!item.poll) return;
    const pollId = item.poll.id;
    const pollType = item.poll.type;
    setConfirmConfig({
      title: t("conversation_card.delete_poll_confirm_title"),
      description: t("conversation_card.delete_poll_confirm_description"),
      confirmLabel: t("conversation_card.delete"),
      destructive: true,
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          const typeLabel =
            pollType === "form"
              ? "form"
              : pollType === "date"
                ? "date poll"
                : pollType === "quizz"
                  ? "quizz"
                  : "poll";
          deleteVotesByPollId(pollId);
          const result = await deletePollWithCascade(pollId, {
            deleteConversation: true,
          });
          if (result.success) {
            toast({
              title: t("conversation_card.poll_deleted_title", {
                type: typeLabel,
              }),
              description: t("conversation_card.poll_deleted_description", {
                type: typeLabel,
              }),
            });
            onRefresh();
          } else {
            throw ErrorFactory.storage(
              result.error || "Failed to delete poll",
              "Impossible de supprimer le sondage",
            );
          }
        } catch (error) {
          logError(error, {
            component: "ConversationCard",
            operation: "deletePoll",
          });
          toast({
            title: t("conversation_card.error"),
            description: (error as Error).message,
            variant: "destructive",
          });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const unifiedPoll: UnifiedPoll | null = item.poll
    ? {
        id: item.poll.id,
        type: item.poll.type,
        title: item.poll.title,
        description: item.poll.description,
        slug: item.poll.slug,
        poll_data_json: item.poll as any,
        creator_id: item.poll.creator_id,
        creator_type: item.poll.creator_type,
        guest_fingerprint: item.poll.guest_fingerprint,
        guest_session_id: item.poll.guest_session_id,
        status: item.poll.status,
        created_at: item.poll.created_at,
        updated_at: item.poll.updated_at,
      }
    : null;

  const handleExport = (format: "csv" | "pdf" | "json" | "markdown") => {
    if (!item.poll || item.poll.type !== "form" || !unifiedPoll) {
      toast({
        title: t("conversation_card.export_unsupported_title"),
        description: t("conversation_card.export_unsupported_description"),
        variant: "destructive",
      });
      return;
    }

    try {
      switch (format) {
        case "csv":
          exportFormPollToCSV(unifiedPoll);
          toast({
            title: t("conversation_card.export_csv_success_title"),
            description: t("conversation_card.export_csv_success_description"),
          });
          break;
        case "pdf":
          exportFormPollToPDF(unifiedPoll);
          toast({
            title: t("conversation_card.export_pdf_title"),
            description: t("conversation_card.export_pdf_description"),
          });
          break;
        case "json":
          exportFormPollToJSON(unifiedPoll);
          toast({
            title: t("conversation_card.export_json_success_title"),
            description: t("conversation_card.export_json_success_description"),
          });
          break;
        case "markdown":
          exportFormPollToMarkdown(unifiedPoll);
          toast({
            title: t("conversation_card.export_markdown_success_title"),
            description: t(
              "conversation_card.export_markdown_success_description",
            ),
          });
          break;
      }
    } catch (err) {
      logError(
        err instanceof Error
          ? err
          : ErrorFactory.api(
              "Export error",
              "Erreur lors de l'export du sondage",
            ),
        {
          component: "ConversationCard",
          operation: "handleExport",
          pollId: item.poll.id,
        },
      );
      toast({
        title: t("conversation_card.export_error_title"),
        description:
          err instanceof Error
            ? err.message
            : t("conversation_card.export_error_description"),
        variant: "destructive",
      });
    }
  };

  return (
    <div
      className={`bg-[#3c4043] rounded-lg shadow-sm border transition-all cursor-pointer relative ${
        isSelected
          ? `${theme.activeBorder} ring-2 ${theme.ring}`
          : "border-gray-700 hover:shadow-md"
      }`}
      data-testid="poll-item"
    >
      {/* Checkbox de sélection */}
      {onToggleSelection && (
        <div
          className="absolute top-4 right-4 z-10"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer ${
              isSelected
                ? theme.checkbox
                : "bg-gray-600 border-gray-400 hover:border-blue-400"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggleSelection();
            }}
          >
            {isSelected && <Check className="w-4 h-4 text-white" />}
          </div>
        </div>
      )}

      <div className="p-3 sm:p-6" onClick={handleCardClick}>
        {/* Header : Titre du poll (priorité) ou conversation */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 pr-10">
            <div className="flex items-center gap-2 mb-2">
              {item.poll ? (
                item.poll.type === "form" ? (
                  <ClipboardList className="w-5 h-5 text-violet-400 flex-shrink-0" />
                ) : item.poll.type === "availability" ? (
                  <Calendar className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                ) : (
                  <Calendar className="w-5 h-5 text-blue-400 flex-shrink-0" />
                )
              ) : (
                <MessageSquare className="w-5 h-5 text-gray-400 flex-shrink-0" />
              )}
              <h3 className="text-base sm:text-lg font-semibold text-white line-clamp-1 sm:line-clamp-2">
                {item.poll ? item.poll.title : item.conversationTitle}
              </h3>

              {/* Badge statut du poll - À côté du titre */}
              {item.poll && (
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusColor(
                    item.poll.status,
                  )}`}
                >
                  {getStatusLabel(item.poll.status)}
                </span>
              )}

              {/* Diagnostic: Badge pour pollId manquant */}
              {!item.poll && (item as any).pollId && (
                <span className="px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 bg-orange-500/20 text-orange-400 border border-orange-500/50">
                  {t("conversation_card.poll_missing_badge")}
                </span>
              )}

              {/* Chevron Mobile */}
              <ChevronDown
                className={cn(
                  "w-5 h-5 text-gray-400 ml-auto sm:hidden transition-transform duration-200",
                  isExpanded ? "rotate-180" : "",
                )}
              />
            </div>

            {/* Collapsible Content Wrapper */}
            <div className={cn(!isExpanded && "hidden sm:block")}>
              {/* Description du poll */}
              {item.poll?.description && (
                <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                  {item.poll.description}
                </p>
              )}

              {/* Lieu de l'événement */}
              {item.poll?.settings?.eventLocation && (
                <div className="flex items-center gap-2 text-sm text-gray-300 mb-3">
                  <MapPin className="w-4 h-4" />
                  <span>{item.poll.settings.eventLocation}</span>
                </div>
              )}

              {/* Diagnostic: Afficher le pollId manquant */}
              {!item.poll && (item as any).pollId && (
                <p className="text-orange-400 text-xs mb-3 font-mono">
                  {t("conversation_card.poll_id_missing", {
                    pollId: (item as any).pollId,
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Collapsible details (Stats, Tags, Dates, Actions) */}
        <div className={cn(!isExpanded && "hidden sm:block")}>
          {/* Statistiques (si poll existe) */}
          {item.poll && (
            <div className="mb-4">
              <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-400 mb-2 sm:mb-3">
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">
                    {item.poll.participants_count || 0} participant
                    {(item.poll.participants_count || 0) > 1 ? "s" : ""}
                  </span>
                  <span className="sm:hidden">
                    {item.poll.participants_count || 0} part.
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Vote className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">
                    {item.poll.type === "form" ? (
                      <>
                        {item.poll.votes_count || 0} réponse
                        {(item.poll.votes_count || 0) > 1 ? "s" : ""}
                      </>
                    ) : (
                      <>
                        {item.poll.votes_count || 0} vote
                        {(item.poll.votes_count || 0) > 1 ? "s" : ""}
                      </>
                    )}
                  </span>
                  <span className="sm:hidden">
                    {item.poll.votes_count || 0}{" "}
                    {item.poll.type === "form" ? "rép." : "votes"}
                  </span>
                </div>
              </div>

              {/* Meilleures dates (pour sondages de dates) */}
              {item.poll.topDates && item.poll.topDates.length > 0 ? (
                <div className="mb-3">
                  <div className="text-xs text-gray-400 mb-2 font-medium">
                    🏆 {t("conversation_card.popular_dates")}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {item.poll.topDates.map((dateInfo, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          index === 0
                            ? "bg-blue-900/30 text-blue-400"
                            : "bg-purple-900/30 text-purple-400"
                        }`}
                      >
                        {index === 0 && " "}
                        {formatDateFull(dateInfo.date)}
                        <span className="ml-1 text-xs opacity-75">
                          (
                          {t("conversation_card.points", {
                            score: dateInfo.score,
                          })}
                          )
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                item.poll.votes_count > 0 &&
                item.poll.type !== "form" && (
                  <div className="mb-3 text-xs text-gray-400">
                    {t("conversation_card.no_favorable_date")}
                  </div>
                )
              )}
            </div>
          )}

          {/* Badge IA avec bouton reprendre conversation */}
          {item.hasAI && (
            <div className="mb-3 flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${theme.lightBadge}`}
              >
                💬 {t("conversation_card.created_by_ai")}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/workspace/date?conversationId=${item.id}`);
                }}
                className={`text-xs transition-colors ${theme.linkText}`}
                data-testid="conversationcard-reprendre-la-conversation-"
              >
                {t("conversation_card.resume_conversation_arrow")}
              </button>
            </div>
          )}

          {/* Tags et Dossier */}
          {(item.tags && item.tags.length > 0) || folder ? (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {folder && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-700 text-gray-300">
                  <Folder className="w-3 h-3" />
                  {folder.icon} {folder.name}
                </span>
              )}
              {item.tags?.map((tagName) => {
                const tag = tags.find((t) => t.name === tagName);
                return (
                  <span
                    key={tagName}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-white"
                    style={{ backgroundColor: tag?.color || "#3b82f6" }}
                  >
                    <Tag className="w-3 h-3" />
                    {tagName}
                  </span>
                );
              })}
            </div>
          ) : null}

          {/* Dates */}
          <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
            {item.poll && item.poll.created_at && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(item.poll.created_at).toLocaleDateString("fr-FR")}
              </span>
            )}
            <span>
              {t("conversation_card.conversation_label")}{" "}
              {item.conversationDate.toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>

          {/* Actions */}
          <div
            className="flex items-center gap-2 flex-wrap"
            onClick={(e) => e.stopPropagation()}
          >
            {item.poll ? (
              <>
                {/* Actions principales pour poll : Résultats et Voter */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/poll/${item.poll!.slug}/results`);
                  }}
                  className="bg-[#1e1e1e] text-gray-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-[#2a2a2a] transition-colors flex items-center justify-center gap-1"
                  data-testid="conversationcard-navigate"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="hidden lg:inline">
                    {t("conversation_card.results")}
                  </span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/poll/${item.poll!.slug}/vote`);
                  }}
                  className={`${theme.primaryButton} text-white px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1`}
                  data-testid="conversationcard-vote"
                >
                  <Vote className="w-4 h-4" />
                  <span className="hidden lg:inline">
                    {t("conversation_card.vote")}
                  </span>
                </button>

                {/* Menu avec toutes les autres actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600"
                      aria-label={t("conversation_card.actions_menu")}
                      data-testid="conversationcard-button"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTagsFolderDialog(true);
                      }}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t("conversation_card.manage_tags_folder")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyLink();
                      }}
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          {t("conversation_card.link_copied_menu")}
                        </>
                      ) : (
                        <>
                          <Share2 className="w-4 h-4 mr-2" />
                          {t("conversation_card.copy_link")}
                        </>
                      )}
                    </DropdownMenuItem>
                    {item.poll.type === "form" &&
                      hasExportableData(unifiedPoll) && (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <Download className="w-4 h-4 mr-2" />
                            {t("conversation_card.export")}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExport("csv");
                              }}
                            >
                              📊 CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExport("pdf");
                              }}
                            >
                              📄 PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExport("json");
                              }}
                            >
                              🔧 JSON
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExport("markdown");
                              }}
                            >
                              📝 Markdown
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      )}
                    <DropdownMenuItem
                      data-testid="conversationcard-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit();
                      }}
                      onMouseEnter={handlePreloadEdit}
                      onMouseLeave={handleMouseLeaveEdit}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      {t("conversation_card.edit")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate();
                      }}
                      data-testid="conversationcard-duplicate"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {t("conversation_card.duplicate")}
                    </DropdownMenuItem>
                    {item.poll.status === "active" && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClose();
                        }}
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        {t("conversation_card.close")}
                      </DropdownMenuItem>
                    )}
                    {item.poll.status !== "archived" && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchive();
                        }}
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        {t("conversation_card.archive")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePoll();
                      }}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t("conversation_card.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {/* Actions pour conversation seule */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/workspace/date?conversationId=${item.id}`);
                  }}
                  className={`${theme.primaryButton} text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2`}
                  data-testid="conversationcard-navigate"
                >
                  <MessageSquare className="w-4 h-4" />
                  {t("conversation_card.resume")}
                </button>

                {/* Menu pour gérer tags/dossier */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600"
                      aria-label={t("conversation_card.actions_menu")}
                      data-testid="conversation-menu-button"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTagsFolderDialog(true);
                      }}
                      data-testid="manage-tags-folder-menu-item"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t("conversation_card.manage_tags_folder")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConversation();
                      }}
                      className="text-red-600 focus:text-red-600"
                      disabled={isDeleting}
                      data-testid="delete-conversation-menu-item"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t("conversation_card.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          {/* Dialog pour gérer tags et dossier */}
          <ManageTagsFolderDialog
            conversationId={item.id}
            currentTags={item.tags || []}
            currentFolderId={item.folderId}
            open={showTagsFolderDialog}
            onOpenChange={setShowTagsFolderDialog}
            onSuccess={onRefresh}
            data-testid="manage-tags-dialog"
          />

          {/* Dialog de confirmation générique (delete poll, archive,
              close, delete conversation) — remplace tous les window.confirm
              qui freezaient le main thread. */}
          <AlertDialog
            open={!!confirmConfig}
            onOpenChange={(open) => {
              if (!open && !isConfirming) setConfirmConfig(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {confirmConfig?.title ?? ""}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirmConfig?.description ?? ""}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isConfirming}>
                  {t("conversation_card.cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={runConfirm}
                  disabled={isConfirming}
                  className={
                    confirmConfig?.destructive
                      ? "bg-red-600 hover:bg-red-700"
                      : undefined
                  }
                  data-testid="confirm-action"
                >
                  {isConfirming
                    ? "..."
                    : (confirmConfig?.confirmLabel ??
                      t("conversation_card.confirm"))}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};
